import React, { useState } from 'react';
import {
  SalesIntelligence,
  CallPhaseAnalysis,
  ToneAnalysis,
  ToneScore,
  getPhaseLabel,
  getBuyingSignalColor,
} from '../adherence/types';
import ManagerReviewPanel from '../adherence/ManagerReviewPanel';
import type { CallInsightsAnalysis } from '../adherence/types';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface SalesIntelligenceV2SectionProps {
  sessionId: string;
  intelligence: SalesIntelligence;
  callInsights: CallInsightsAnalysis;
  speechRateAlerts: SpeechRateAlert[];
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

interface ToneBarRowProps {
  label: string;
  toneScore: ToneScore;
  isPositive: boolean;
}

const ToneBarRow: React.FC<ToneBarRowProps> = ({ label, toneScore, isPositive }) => {
  const fillColor = isPositive
    ? toneScore.score >= 60 ? 'bg-green-500' : toneScore.score >= 30 ? 'bg-green-300' : 'bg-gray-200'
    : toneScore.score >= 60 ? 'bg-red-500' : toneScore.score >= 30 ? 'bg-red-300' : 'bg-gray-200';
  const textColor = isPositive ? 'text-green-700' : 'text-red-700';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${textColor}`}>{toneScore.score}%</span>
          <span className="text-[10px] text-gray-400">(AI confidence: {toneScore.confidence_level}%)</span>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${fillColor}`}
          style={{ width: `${toneScore.score}%` }}
        />
      </div>
      {toneScore.explanation && (
        <p className="text-[11px] text-gray-500 leading-tight">{toneScore.explanation}</p>
      )}
    </div>
  );
};

const SalesIntelligenceV2Section: React.FC<SalesIntelligenceV2SectionProps> = ({
  sessionId,
  intelligence,
  callInsights,
  speechRateAlerts,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
}) => {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);

  if (!intelligence) {
    return (
      <div className="text-center py-12 text-gray-500">
        No sales intelligence data available for this session.
      </div>
    );
  }

  const phases = intelligence?.phases ?? [];

  const togglePhase = (idx: number) => {
    setExpandedPhase((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="space-y-5">
      {/* 1. Summary bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">Overall Session Customer Buying Signal</p>
            <span
              className={`inline-block text-sm font-semibold px-3 py-1 rounded capitalize ${getBuyingSignalColor(
                intelligence?.overall_session_signal ?? ''
              )}`}
            >
              {intelligence?.overall_session_signal?.replace(/_/g, ' ') ?? 'Unknown'}
            </span>
            {typeof intelligence?.overall_session_signal_score === 'number' && (
              <span className="ml-2 text-xs text-gray-500">
                {Math.round(intelligence.overall_session_signal_score)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Closing Customer Buying Signal</p>
            <span
              className={`inline-block text-sm font-semibold px-3 py-1 rounded capitalize ${getBuyingSignalColor(
                intelligence?.closing_signal ?? ''
              )}`}
            >
              {intelligence?.closing_signal?.replace(/_/g, ' ') ?? 'Unknown'}
            </span>
            {typeof intelligence?.closing_signal_score === 'number' && (
              <span className="ml-2 text-xs text-gray-500">
                {Math.round(intelligence.closing_signal_score)}%
              </span>
            )}
          </div>
        </div>
        {intelligence?.phase_narrative && (
          <p className="text-sm text-gray-700">{intelligence.phase_narrative}</p>
        )}
      </div>

      {/* 2. Phase detail cards */}
      {phases.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-700">Call Phase Details ({phases.length})</p>
          {phases.map((phase: CallPhaseAnalysis, idx: number) => {
            const isExpanded = expandedPhase === idx;

            return (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Phase header */}
                <button
                  onClick={() => togglePhase(idx)}
                  className="w-full flex items-start justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  type="button"
                >
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold text-gray-800">{getPhaseLabel(phase.phase)}</p>
                    <div className="space-y-0.5 text-xs text-gray-600">
                      <p>
                        <span className="font-medium text-gray-500">Customer Buying Signal:</span>{' '}
                        <span className="capitalize">{phase.buying_signal?.replace(/_/g, ' ')}</span>
                        {typeof phase.buying_signal_score === 'number' && (
                          <span className="text-gray-400 ml-1">({phase.buying_signal_score}%)</span>
                        )}
                      </p>
                      <p>
                        <span className="font-medium text-gray-500">Customer Emotional Tone:</span>{' '}
                        <span className="capitalize">{phase.emotional_tone?.replace(/_/g, ' ')}</span>
                        {typeof phase.emotional_tone_score === 'number' && (
                          <span className="text-gray-400 ml-1">({phase.emotional_tone_score}%)</span>
                        )}
                      </p>
                      {phase.conversation_control && (
                        <p>
                          <span className="font-medium text-gray-500">Conversation Control:</span>{' '}
                          <span className="capitalize">{phase.conversation_control?.replace(/_/g, ' ')}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-gray-400 text-xs transition-transform duration-150 flex-shrink-0 mt-1"
                    style={{
                      transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      display: 'inline-block',
                    }}
                  >
                    &#9660;
                  </span>
                </button>

                {/* Phase body */}
                {isExpanded && (
                  <div className="p-4 border-t border-gray-200 space-y-3">
                    {phase.trigger && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-2">
                        <span className="text-xs font-semibold text-amber-700 uppercase">Trigger / Reason: </span>
                        <span className="text-sm text-amber-900">{phase.trigger}</span>
                      </div>
                    )}

                    {phase.phase_summary && (
                      <p className="text-sm text-gray-700">{phase.phase_summary}</p>
                    )}

                    {phase.start_approx && (
                      <p className="text-xs text-gray-400">
                        {phase.start_approx}
                        {phase.end_approx ? ` - ${phase.end_approx}` : ''}
                      </p>
                    )}

                    {/* View in Timeline */}
                    {(phase.primary_timestamp || phase.supporting_quotes?.[0]?.timestamp_approx) && (
                      <button
                        type="button"
                        onClick={() => onNavigateToTimestamp?.(
                          phase.primary_timestamp ?? phase.supporting_quotes![0].timestamp_approx!
                        )}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                      >
                        View in Timeline &rarr;
                      </button>
                    )}

                    {/* What worked / didn't work */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {phase.what_worked && (
                        <div className="bg-green-50 border border-green-200 rounded p-3">
                          <p className="text-xs font-semibold text-green-700 uppercase mb-1">
                            What Worked
                          </p>
                          <p className="text-sm text-green-900">{phase.what_worked}</p>
                        </div>
                      )}
                      {phase.what_didnt_work && (
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                          <p className="text-xs font-semibold text-red-700 uppercase mb-1">
                            What Could Improve
                          </p>
                          <p className="text-sm text-red-900">{phase.what_didnt_work}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 4. Deal Risk Signals */}
      {intelligence?.deal_risk_signals && intelligence.deal_risk_signals.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-red-700 mb-2">Deal Risk Signals</p>
          <div className="flex flex-wrap gap-2">
            {intelligence.deal_risk_signals.map((risk, idx) => (
              <span
                key={idx}
                className="text-xs bg-red-100 text-red-800 border border-red-300 px-2.5 py-1 rounded-full"
              >
                {risk}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 5. Rapport Moments */}
      {intelligence?.rapport_moments && intelligence.rapport_moments.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Rapport Moments</p>
          <div className="space-y-2">
            {intelligence.rapport_moments.map((rapportMoment, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                {rapportMoment.timestamp_approx && (
                  <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">
                    {rapportMoment.timestamp_approx}
                  </span>
                )}
                <p>{rapportMoment.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Speech Rate Assessment */}
      {(intelligence?.speech_rate_assessment || speechRateAlerts.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Speech Rate Assessment</p>
          {intelligence?.speech_rate_assessment && (
            <p className="text-sm text-gray-700 mb-2">{intelligence.speech_rate_assessment}</p>
          )}
          {speechRateAlerts.length > 0 && (
            <p className="text-xs text-gray-500">
              {speechRateAlerts.length} speech rate alert{speechRateAlerts.length !== 1 ? 's' : ''} during the call
            </p>
          )}
        </div>
      )}

      {/* 7. Rep Tone Analysis */}
      {callInsights?.tone_analysis?.positive && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <p className="text-sm font-semibold text-gray-700">Rep Tone Analysis</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-100 rounded-lg p-3 space-y-3">
              <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wider">Positive</p>
              <ToneBarRow label="Confidence" toneScore={callInsights.tone_analysis.positive.confidence} isPositive={true} />
              <ToneBarRow label="Optimism" toneScore={callInsights.tone_analysis.positive.optimism} isPositive={true} />
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg p-3 space-y-3">
              <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider">Negative</p>
              <ToneBarRow label="Defensiveness" toneScore={callInsights.tone_analysis.negative.defensiveness} isPositive={false} />
              <ToneBarRow label="Confrontation" toneScore={callInsights.tone_analysis.negative.confrontation} isPositive={false} />
            </div>
          </div>
          {callInsights.tone_analysis.overall_coaching && (
            <p className="text-xs text-gray-600 border-t border-gray-100 pt-3">
              {callInsights.tone_analysis.overall_coaching}
            </p>
          )}
        </div>
      )}

      {/* 8. Coaching: This Client */}
      {intelligence?.coaching_for_this_client && intelligence.coaching_for_this_client.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-blue-800 mb-2">Coaching: This Client</p>
          <ol className="space-y-1 list-decimal list-inside">
            {intelligence.coaching_for_this_client.map((step, idx) => (
              <li key={idx} className="text-sm text-blue-900">
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* 9. Coaching: General Improvement */}
      {intelligence?.coaching_for_general_improvement &&
        intelligence.coaching_for_general_improvement.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-indigo-800 mb-2">Coaching: General Improvement</p>
            <ol className="space-y-1 list-decimal list-inside">
              {intelligence.coaching_for_general_improvement.map((tip, idx) => (
                <li key={idx} className="text-sm text-indigo-900">
                  {tip}
                </li>
              ))}
            </ol>
          </div>
        )}

      {/* 10. Manager Review */}
      <ManagerReviewPanel
        sessionId={sessionId}
        currentSection="sales_intelligence"
        managerReview={callInsights?.manager_review}
        isAdmin={isAdmin}
        onReviewSaved={onReviewSaved}
      />
    </div>
  );
};

export default SalesIntelligenceV2Section;
